import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useLanguage, useT } from "@/lib/LanguageContext";
import { ArrowLeft, Mail } from "lucide-react";
import TermsAndConditions from "./TermsAndConditions";
import SupportTicketModal from "../components/support/SupportTicketModal";
import legalFr from "../i18n/legal/fr";
import legalEn from "../i18n/legal/en";
import legalEs from "../i18n/legal/es";

// Last updated date for privacy content
const LAST_UPDATED = "2026-10-01";

const LEGAL_DOCS = { fr: legalFr, en: legalEn, es: legalEs };

export default function LegalPage() {
  const { doc } = useParams();
  const { lang } = useLanguage();
  const t = useT("saas");
  const tCommon = useT("common");
  const docKey = doc === "privacy" ? "privacy" : "terms";

  const [supportModalOpen, setSupportModalOpen] = useState(false);

  const L = lang === "fr" || lang === "en" || lang === "es" ? lang : "en";
  const langDocs = LEGAL_DOCS[L] || LEGAL_DOCS.en;
  const data = langDocs[docKey] || langDocs.privacy;
  const title = t(docKey === "privacy" ? "footer.privacy" : "footer.terms");

  useEffect(() => {
    if (docKey === "privacy") {
      document.title = `${title} — Solunary`;
    }
  }, [title, docKey]);

  // Delegate terms view to the comprehensive TermsAndConditions component
  if (docKey === "terms") {
    return <TermsAndConditions />;
  }

  return (
    <main className="bg-sol-ivory min-h-screen flex flex-col justify-between" data-testid={`legal-${doc}`}>
      <header className="bg-sol-midnight text-sol-ivory">
        <div className="max-w-3xl mx-auto px-6 py-6 flex items-center justify-between">
          <Link to="/platform" className="flex items-center gap-2.5">
            <img src="/brand/solunary-logo.png" alt="Solunary" className="h-12 w-12 object-contain" />
            <span className="font-sol font-semibold text-lg">Solunary</span>
          </Link>
          <Link to="/platform" className="flex items-center gap-1.5 text-sm text-sol-ivory/70 hover:text-sol-gold transition">
            <ArrowLeft className="w-4 h-4" /> {tCommon("actions.back")}
          </Link>
        </div>
      </header>

      <article className="max-w-3xl mx-auto px-6 py-16 flex-1 w-full">
        <h1 className="font-display text-4xl text-sol-midnight mb-2">{title}</h1>
        <p className="text-sm text-sol-midnight/50 mb-10">
          {data.lastUpdatedLabel} : {LAST_UPDATED}
        </p>
        <div className="space-y-8">
          {data.sections.map((section) => (
            <section key={section.id}>
              <h2 className="font-sol font-semibold text-lg text-sol-midnight mb-2">{section.heading}</h2>
              {section.paragraphs.map((paragraph, i) => (
                <p key={i} className="text-sol-midnight/70 leading-relaxed">{paragraph}</p>
              ))}
            </section>
          ))}
        </div>

        {/* Contact and Support card */}
        <div className="mt-14 p-6 sm:p-8 rounded-3xl bg-white border border-sol-midnight/10 shadow-sm flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div>
            <h3 className="font-sol font-semibold text-sol-midnight text-base mb-1">
              Questions regarding our Privacy Policy?
            </h3>
            <p className="text-xs text-sol-midnight/60">
              Our Data Protection Officer can be reached directly at{" "}
              <a href="mailto:hey@solunary.health" className="text-sol-gold font-medium hover:underline">
                hey@solunary.health
              </a>.
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <button
              type="button"
              onClick={() => setSupportModalOpen(true)}
              data-testid="privacy-support-ticket-btn"
              className="px-4 py-2.5 bg-sol-gold hover:bg-sol-gold/90 text-sol-midnight font-semibold text-xs rounded-xl transition inline-flex items-center gap-2 shadow-sm"
            >
              <Mail className="w-4 h-4" />
              <span>Raise Ticket</span>
            </button>
          </div>
        </div>
      </article>

      <footer className="border-t border-sol-midnight/10 bg-white py-6 px-6 mt-16">
        <div className="max-w-3xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-sol-midnight/60">
          <div>© {new Date().getFullYear()} Solunary. {t("footer.rights") || "All rights reserved."}</div>
          <div className="flex items-center gap-4">
            <Link to="/terms" className="hover:text-sol-gold transition">
              {t("footer.terms") || "Terms"}
            </Link>
            <Link to="/contact" className="hover:text-sol-gold transition">
              Support
            </Link>
            <a href="mailto:hey@solunary.health" className="text-sol-gold hover:underline">
              hey@solunary.health
            </a>
          </div>
        </div>
      </footer>

      <SupportTicketModal
        isOpen={supportModalOpen}
        onClose={() => setSupportModalOpen(false)}
        defaultCategory="legal"
        defaultSubject="Privacy Policy and Data Protection Inquiry"
      />
    </main>
  );
}

